import { useMemo } from 'react';
import type { ConsignmentLineItem, PalletViewRow } from '@/entities/consignments';
import type { LocalPallet } from './types';

export interface PalletGroup {
  pallet: LocalPallet;
  lines: ConsignmentLineItem[];
}

export interface UsePalletGroupingResult {
  unassignedLines: ConsignmentLineItem[];
  palletGroups: PalletGroup[];
  hasSelectedInPallets: boolean;
}

/**
 * Hook for grouping lines by pallets
 * Separates business logic from UI components
 */
export function usePalletGrouping(
  tableData: PalletViewRow[],
  pallets: LocalPallet[],
  selectedLineIds: string[]
): UsePalletGroupingResult {
  // Group lines by pallet assignment
  const { unassignedLines, palletGroups } = useMemo(() => {
    const unassigned: ConsignmentLineItem[] = [];
    const byPallet = new Map<string, ConsignmentLineItem[]>();

    // Initialize all pallets
    for (const pallet of pallets) {
      byPallet.set(pallet.id, []);
    }

    // Group lines based on their parentId (pallet assignment)
    for (const row of tableData) {
      // Skip pallet header rows
      if ('palletNumber' in row) continue;

      const line = row as ConsignmentLineItem;
      const parentId = 'parentId' in row ? (row as { parentId?: string }).parentId : undefined;

      if (parentId && byPallet.has(parentId)) {
        byPallet.get(parentId)!.push(line);
      } else if (!parentId) {
        unassigned.push(line);
      }
    }

    return {
      unassignedLines: unassigned,
      palletGroups: pallets.map((p) => ({
        pallet: p,
        lines: byPallet.get(p.id) || []
      }))
    };
  }, [tableData, pallets]);

  // Check if any selected lines are assigned to pallets
  const hasSelectedInPallets = useMemo(() => {
    return selectedLineIds.some((id) => palletGroups.some((group) => group.lines.some((line) => line.id === id)));
  }, [selectedLineIds, palletGroups]);

  return {
    unassignedLines,
    palletGroups,
    hasSelectedInPallets
  };
}
